import { useMemo } from "react";

import { SUBJECT_CATEGORIES } from "../config/subjectConfig";

export default function useTeacherSubjects(rooms, query, selectedSubject) {
  /**
   * =========================================================
   * ✅ FLATTEN TEACHERS
   * =========================================================
   */
  const teachers = useMemo(() => {
    const result = [];

    rooms.forEach((room) => {
      room.teacherSlots?.forEach((slot) => {
        if (!slot.reservedBy) return;

        result.push({
          name: slot.reservedBy.name,
          subjects: slot.reservedBy.subjects || [],
          roomName: room.name,
          endTime: slot.endTime,
        });
      });
    });

    return result;
  }, [rooms]);

  /**
   * =========================================================
   * ✅ GROUP BY SUBJECT
   * =========================================================
   */
  const grouped = useMemo(() => {
    const map = {};

    teachers.forEach((teacher) => {
      teacher.subjects.forEach((subject) => {
        if (!map[subject.name]) {
          map[subject.name] = [];
        }

        map[subject.name].push(teacher);
      });
    });

    return map;
  }, [teachers]);

  /**
   * =========================================================
   * ✅ SUBJECT LIST
   * =========================================================
   */
  const subjects = useMemo(() => {
    return Object.keys(grouped)
      .filter((subject) =>
        subject.toLowerCase().includes(query.trim().toLowerCase()),
      )
      .sort((a, b) => a.localeCompare(b, "de"));
  }, [grouped, query]);

  /**
   * =========================================================
   * ✅ CATEGORY GROUPING
   * =========================================================
   */
  const categorizedSubjects = useMemo(() => {
    const result = {};

    Object.keys(SUBJECT_CATEGORIES).forEach((category) => {
      result[category] = [];
    });

    result.Andere = [];

    subjects.forEach((subject) => {
      let assigned = false;

      for (const [category, categorySubjects] of Object.entries(
        SUBJECT_CATEGORIES,
      )) {
        if (categorySubjects.includes(subject)) {
          result[category].push(subject);
          assigned = true;
          break;
        }
      }

      if (!assigned) {
        result.Andere.push(subject);
      }
    });

    return result;
  }, [subjects]);

  /**
   * =========================================================
   * ✅ SELECTED TEACHERS
   * =========================================================
   */
  const teachersForSelected = useMemo(() => {
    if (!selectedSubject) return [];

    return [...(grouped[selectedSubject] || [])].sort(
      (a, b) => (b.endTime || 0) - (a.endTime || 0),
    );
  }, [grouped, selectedSubject]);

  return {
    teachers,
    grouped,
    subjects,
    categorizedSubjects,
    teachersForSelected,
  };
}
